// Birthday heads-ups on My Desk — owner, 2026-08-19.
//
// Reads the people roster primed at sign-in (lib/bootstrap.ts, key
// `people-roster`). Home must not fetch on mount (INV-1) — deliberately a
// non-fetching `peekResource`, not `useResource`: unlike ComplianceAlerts.tsx
// (which fetches its own key because DeskGrid can mount it BEFORE bootstrap
// has answered, gated by a `hasContent` check that only mounts it once
// there is something to show), `birthdays` carries no `hasContent` entry in
// panelCatalog.tsx and always mounts — so a `useResource` call here would
// fire from every test in this app that puts a `birthdays` panel on a
// canvas without also stubbing `/api/people`, of which there are many. The
// staleness this shared with ComplianceAlerts (bootstrap skipping a refetch
// because SOME cached answer already exists, mirrored or not) is fixed at
// its actual source instead — lib/bootstrap.ts's `keepPeople`, via
// `resourcePendingFetch` — so this file does not need to fetch on its own.
import type { JSX } from 'preact';
import { useRef } from 'preact/hooks';
import { birthdaySoon, daysUntilBirthday } from '../lib/birthday';
import { useSparkleOnView } from '../lib/sparkleOnView';
import { LOCATION_NAMES, type LocationCode } from '../lib/locations';
import { peekResource } from '../lib/store';
import { visibleClinics } from './ClinicTabs';
import { DeskPanel } from './DeskPanel';

const ROSTER_KEY = 'people-roster';

interface RosterPerson {
  id: number;
  full_name: string;
  birthday_md: string | null;
  home_location_code: string;
  location_name: string;
  status: string | null;
}

interface PeopleRoster {
  people: RosterPerson[];
}

function timingLabel(birthdayMd: string, now: Date): string {
  const days = daysUntilBirthday(birthdayMd, now);
  if (days === 0) return 'today';
  return `in ${days} days`;
}

/** People with a birthday in the next five clinic days, scoped to visible clinics. */
export function birthdayAlertRows(
  roster: PeopleRoster | null,
  clinics: readonly LocationCode[],
  now: Date,
): RosterPerson[] {
  const clinicSet = new Set(clinics);
  const people = roster?.people ?? [];
  return people
    .filter((p) =>
      p.status !== 'TERMINATED'
      && clinicSet.has(p.home_location_code as LocationCode)
      && birthdaySoon(p.birthday_md, now))
    .sort((a, b) => {
      const da = daysUntilBirthday(a.birthday_md, now) ?? 99;
      const db = daysUntilBirthday(b.birthday_md, now) ?? 99;
      if (da !== db) return da - db;
      return a.full_name.localeCompare(b.full_name);
    });
}

export function BirthdayAlerts(
  { now = new Date(), chromeless = false }: { now?: Date; chromeless?: boolean } = {},
): JSX.Element {
  const roster = peekResource<PeopleRoster>(ROSTER_KEY);
  const rows = birthdayAlertRows(roster, visibleClinics(), now);
  const wrapRef = useRef<HTMLDivElement>(null);
  useSparkleOnView(wrapRef, rows.map((p) => p.id).join(','));

  return (
    <DeskPanel
      title="Birthdays"
      subtitle="Birthdays in the next 5 days."
      testId="birthday-alerts-panel"
      chromeless={chromeless}
    >
      <div class="queue desk-panel-scroll" data-testid="birthday-alerts" ref={wrapRef}>
        {rows.length === 0 ? (
          <p class="empty">No birthdays in the next 5 days.</p>
        ) : (
          rows.map((p) => (
            <div class="q birthday-alert" key={p.id} data-sparkle="">
              <div class="code">{LOCATION_NAMES[p.home_location_code as LocationCode] ?? p.location_name}</div>
              <div>
                <b>{p.full_name}</b>
                <span>
                  {p.birthday_md} · {timingLabel(p.birthday_md!, now)}
                </span>
              </div>
            </div>
          ))
        )}
      </div>
    </DeskPanel>
  );
}
